/**
 * Dark mode wiring, shared by the React side and the inline no-flash script in
 * `index.html`. That script cannot import this file, so it repeats the storage
 * key and attribute name as literals — `noFlashScript.test.ts` reads
 * `index.html` and fails if the two drift apart.
 */

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = 'light' | 'dark';

/**
 * Users who never chose follow their OS (Settings design: System is the
 * default). Only once the dark-mode flag is on: until then ThemeSync keeps the
 * page light whatever this says.
 *
 * The inline no-flash script in index.html deliberately does NOT apply this
 * default — it only darkens for a *stored* preference. It runs before flags
 * load, so defaulting to the OS there would flash dark for every flag-off user
 * on a dark OS. ThemeSync stores the preference once the flag is known, so a
 * flag-on user's first visit paints light briefly; every later load is right.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

/**
 * localStorage mirror of the preference. Server preferences arrive after boot,
 * so this is what the inline script reads to paint the right theme first.
 */
export const THEME_STORAGE_KEY = 'kula-theme';

/** Key in the server-side user preferences (`/user_preference`). */
export const THEME_PREFERENCE_KEY = 'theme_preference';

/**
 * Set on `<html>` as `data-kula-theme="dark"`. Not Chakra's own `data-theme`:
 * Chakra's ColorModeProvider rewrites that attribute to `light` on every mount.
 */
export const THEME_ATTRIBUTE = 'data-kula-theme';

export const DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/** LaunchDarkly `fe-dark-mode`, read through `useKFlags()`. */
export const DARK_MODE_FLAG = 'feDarkMode';
